import {
  BrandsIcon,
  ExploreIcon,
  GoogleCampaignsIcon,
  ImageAdsIcon,
  OrganicCampaignsIcon,
  PaidCampaignsIcon,
  ProductShowcaseIcon,
  ProductsIcon,
  VideoAdsIcon,
  type SidebarIcon,
} from "./sidebar-icons";

export type NavItem = {
  name: string;
  href: string;
  icon: SidebarIcon;
  highlightId?: string;
  subItems?: Array<{ name: string; href: string }>;
};

export type NavSection = {
  /** Section header text rendered above the following items. */
  header: string;
};

export type NavEntryV2 = NavItem | NavSection;

export function isNavSection(entry: NavEntryV2): entry is NavSection {
  return "header" in entry;
}

/**
 * Sidebar nav config — single canonical IA (Zeely-style flat artifact shape
 * plus the legacy `Google campaigns` Promotion item).
 *
 * The `_wsSlug` argument is unused today — short URLs resolve the active
 * workspace via the default-workspace store inside each page.
 */
export function getSidebarNavItemsV2(_wsSlug: string | null): NavEntryV2[] {
  return [
    { name: "Home", href: "/explore", icon: ExploreIcon },
    { name: "Brand DNA", href: "/brand-dna", icon: BrandsIcon },
    { name: "Products", href: "/products", icon: ProductShowcaseIcon },
    { header: "Creative Tools" },
    {
      name: "Image ads",
      href: "/static-ads",
      icon: ImageAdsIcon,
      highlightId: "sidebar-nav-static-ads",
    },
    {
      name: "Video ads",
      href: "/video-ads",
      icon: VideoAdsIcon,
      highlightId: "sidebar-nav-video-ads",
    },
    { name: "Projects", href: "/projects", icon: ProductsIcon },
    { header: "Promotion" },
    {
      name: "Meta campaigns",
      href: "/paid-campaigns",
      icon: PaidCampaignsIcon,
      highlightId: "sidebar-nav-meta-campaigns",
    },
    { name: "Google campaigns", href: "/google-campaigns", icon: GoogleCampaignsIcon },
    { name: "Social posting", href: "/organic-campaigns", icon: OrganicCampaignsIcon },
  ];
}

/** Mobile drawer + bottom-nav fallback consume the flat list of `NavItem`s. */
export function getMobileNavItemsV2(wsSlug: string | null): NavItem[] {
  return getSidebarNavItemsV2(wsSlug).filter((entry): entry is NavItem => !isNavSection(entry));
}

export function getSidebarNavItemByHref(href: string, wsSlug: string | null = null): NavItem | null {
  return getMobileNavItemsV2(wsSlug).find((item) => item.href === href) ?? null;
}

/**
 * Returns the single most-specific nav item matching the given pathname
 * (longest-prefix wins). Prevents multiple items from highlighting when one
 * item's href is a prefix of another's.
 */
export function getActiveNavItem(pathname: string, items: NavItem[]): NavItem | null {
  let best: NavItem | null = null;
  for (const item of items) {
    if (pathname === item.href || pathname.startsWith(item.href + "/")) {
      if (!best || item.href.length > best.href.length) {
        best = item;
      }
    }
  }
  return best;
}
